import { FC, useEffect, useId, useState } from 'react'
import cn from 'classnames'
import { User, Cart, Help } from '@components/icons'
import { useUI } from '@corratech/pylot-ui/context'
import { useAuthUI } from '@corratech/pylot-auth-manager'
import { Button } from '@corratech/pylot-ui'
import HelpCenterPanel from '@components/common/HelpCenterPanel/HelpCenterPanel'
import AccountPanel from '@components/common/AccountPanel/AccountPanel'
import s from './UserNav.module.scss'
import { useAccount } from '@corratech/pylot-auth-manager'
import { useTranslation } from 'react-i18next'
import dynamic from 'next/dynamic'
import { useCart } from '@corratech/pylot-cart-manager'
import { Maybe, CartItemInterface } from '@pylot-data/pylotschema'
import {
    customerUpdateEvent,
    identifyEvent
} from '@lib/utils/exponeaTrackingEvents'

const Searchbar = dynamic(() => import('@components/common/Searchbar'), {
    ssr: false
})
interface Props {
    className?: string
    isPWANav?: boolean
}

const getCartItemsQuantity = (items?: Maybe<Maybe<CartItemInterface>[]>) => {
    if (!items) return 0

    let result = 0
    for (const item of items) {
        result = result + (item?.quantity || 0)
    }
    return result
}

const UserNav: FC<Props> = ({ className, isPWANav = false }) => {
    const { t } = useTranslation()
    const { data } = useAccount()
    const { toggleSidebar } = useUI()
    const { openAuthModal, onLoginCallback, onSignUpCallback } = useAuthUI()
    const { data: cartData } = useCart()
    const panelId = useId()
    const [accountOpen, setAccountOpen] = useState(false)
    const [helpOpen, setHelpOpen] = useState(false)

    useEffect(() => {
        const onPanel = (event: Event) => {
            const source = (event as CustomEvent<string>).detail
            if (source === panelId) return
            setAccountOpen(false)
            setHelpOpen(false)
        }
        window.addEventListener('casaideas:user-panel', onPanel)
        return () => window.removeEventListener('casaideas:user-panel', onPanel)
    }, [panelId])

    const openAccountPanel = () => {
        setHelpOpen(false)
        setAccountOpen(true)
        window.dispatchEvent(
            new CustomEvent('casaideas:user-panel', { detail: panelId })
        )
    }

    const openHelpPanel = () => {
        setAccountOpen(false)
        setHelpOpen(true)
        window.dispatchEvent(
            new CustomEvent('casaideas:user-panel', { detail: panelId })
        )
    }

    const numberOfCartItems = getCartItemsQuantity(cartData?.data?.cart?.items)
    const isNumberOfCartItemsDisplayed = numberOfCartItems > 0

    const customer = data?.data?.customer
    const nameInitial = (customer?.firstname ?? '')
        .trim()
        .charAt(0)
        .toUpperCase()

    const [isTokenExist, setIsTokenExist] = useState(false)

    useEffect(() => {
        const checkToken = () =>
            setIsTokenExist(!!window.localStorage.getItem('pylot_token'))
        checkToken()
        window.addEventListener('pylot:refresh-content', checkToken)
        window.addEventListener('storage', checkToken)
        return () => {
            window.removeEventListener('pylot:refresh-content', checkToken)
            window.removeEventListener('storage', checkToken)
        }
    }, [])

    useEffect(() => {
        if (customer?.email) {
            identifyEvent({
                registered: customer.document_number,
                email_id: customer.email
            })
            customerUpdateEvent({
                first_name: customer.firstname,
                last_name: customer.lastname,
                email: customer.email?.toLowerCase(),
                rut: customer.document_number,
                birthday: customer.date_of_birth,
                opt_in_tyc: 1,
                opt_in_ofertas: customer.is_subscribed,
                phone: customer.customer_telephone
            })

            if (cartData?.data?.cart?.shipping_addresses?.[0]) {
                const shippingAddress = cartData.data.cart.shipping_addresses[0]
                customerUpdateEvent({
                    comuna: shippingAddress.city,
                    region: shippingAddress.region?.label,
                    depto_oficina: shippingAddress.street?.[2],
                    direccion: shippingAddress.street?.[0],
                    numero: shippingAddress.street?.[1],
                    codigo_pais: shippingAddress.country?.code
                })
            }
        }
    }, [customer?.email])

    const shouldDisplayUserAvatar = !isTokenExist && !isPWANav

    /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
    return (
        <nav className={cn(s.root, className)}>
            <ul className={s.list}>
                <li className={cn(s['search-menu'])}>
                    <div
                        className={cn(
                            s['search-display'],
                            'animate-fade-in-down'
                        )}
                    >
                        <Searchbar id="desktop-search" variant="header" />
                    </div>
                </li>
                {isTokenExist && !isPWANav && (
                    <li className={cn(s['user-menu'], s.item, s.loggedInItem)}>
                        <Button
                            className={cn(
                                s.avatarButton,
                                s.iconBtn,
                                s.loggedInButton
                            )}
                            variant="link"
                            aria-label={t('Menu')}
                            onClick={openAccountPanel}
                        >
                            <User />
                            {nameInitial ? (
                                <span className={s.initial}>{nameInitial}</span>
                            ) : null}
                        </Button>
                    </li>
                )}
                {shouldDisplayUserAvatar && (
                    <li
                        className={cn(s['user-menu'], s.item, s.itemReset)}
                        onClick={() => {
                            if (!isTokenExist) {
                                openAuthModal()
                            }
                        }}
                    >
                        <Button
                            className={cn(s.avatarButton, s.iconBtn)}
                            variant="link"
                            aria-label={t('Menu')}
                        >
                            <User />
                        </Button>
                    </li>
                )}

                <li className={cn(s.item, s.itemReset, s.stores)}>
                    <Button
                        onClick={openHelpPanel}
                        className={cn(s.avatarButton, s.iconBtn)}
                        variant="link"
                        aria-label={t('Help center', {
                            defaultValue: 'Centro de ayuda'
                        })}
                    >
                        <Help />
                    </Button>
                </li>
                <li className={cn(s.item, s.itemReset)} onClick={toggleSidebar}>
                    <div className="relative flex items-center justify-center">
                        <Button
                            className={cn(s['bag-btn'], s.iconBtn)}
                            variant="link"
                            aria-label={t('Bag')}
                        >
                            <Cart />
                        </Button>
                        {isNumberOfCartItemsDisplayed && (
                            <div
                                className={cn(s['cart-quantity'], {
                                    [s['cart-quantity--large']]:
                                        numberOfCartItems >= 100
                                })}
                            >
                                <p>{numberOfCartItems}</p>
                            </div>
                        )}
                    </div>
                </li>
            </ul>
            <HelpCenterPanel
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
            />
            <AccountPanel
                open={accountOpen}
                onClose={() => setAccountOpen(false)}
                customer={customer}
            />
        </nav>
    )
}

export default UserNav
